import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Switch } from '@sorare/core/src/components/search/Switch';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary, scoutingCenter } from '@sorare/core/src/lib/glossary';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import { ProjectionScoreIcon } from './ProjectionScoreIcon/index.tsx';

const messages: {
  [key in So5FixtureEvent]?: MessageDescriptor | undefined;
} = {
  [So5FixtureEvent.DAILY]: scoutingCenter.projectionDaily,
  [So5FixtureEvent.CLASSIC]: scoutingCenter.projectionClassic,
};

const availableProjectionBySport: Record<Sport, So5FixtureEvent[]> = {
  [Sport.FOOTBALL]: [],
  [Sport.NBA]: [So5FixtureEvent.DAILY, So5FixtureEvent.CLASSIC],
  [Sport.BASEBALL]: [So5FixtureEvent.DAILY, So5FixtureEvent.CLASSIC],
};

export const Projections = () => {
  const { statsManager, updatePlayerSearchViewAttribute } =
    useManagePlayerSearchView();
  const { sport } = useSportContext();

  const projections = statsManager[PlayerSearchViewAttributes.PROJECTION];
  const availableProjections = availableProjectionBySport[sport];
  const onProjectionsChange = (input: So5FixtureEvent[]) =>
    updatePlayerSearchViewAttribute(
      PlayerSearchViewAttributes.PROJECTION,
      input
    );

  if (availableProjections.length === 0) return null;

  return (
    <Vertical>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage {...glossary.projections} />
      </LabelM>
      {availableProjections.map(so5FixtureEvent => (
        <Switch
          key={so5FixtureEvent}
          checked={projections.includes(so5FixtureEvent)}
          onChange={() =>
            onProjectionsChange(
              projections.includes(so5FixtureEvent)
                ? projections.filter(s => s !== so5FixtureEvent)
                : [...projections, so5FixtureEvent]
            )
          }
          label={
            <Horizontal>
              <ProjectionScoreIcon so5FixtureEvent={so5FixtureEvent} />
              {!!messages[so5FixtureEvent] && (
                <FormattedMessage {...messages[so5FixtureEvent]} />
              )}
            </Horizontal>
          }
        />
      ))}
    </Vertical>
  );
};
